import type { TtqSession, TtqSessionParticleEntry } from '../../services/ttqService';
import { resolveKnowledgeParticleQaDisplay } from '../../components/KnowledgeParticleQaSections';
import { resolveTtqParticleTitle } from '../../utils/ttqDisplay';
import type {
  TtqibCanvasObject,
  TtqibLayer,
  TtqibNodeLayout,
  TtqibState,
} from './types';
import {
  derivedLayerId,
  segmentObjectId,
  imageObjectId,
  TTQIB_LAYER_SOURCE_PREFIX,
  wholeObjectId,
} from './types';

const SCHEMA_VERSION = 7;

type LegacyRawState = Partial<TtqibState> & {
  excalidrawScene?: unknown;
  nodes?: TtqibNodeLayout[];
};

function isMultiSolutionRun(run: NonNullable<TtqSession['pathRuns']>[number]): boolean {
  return run.pathId === 'multi_solution' || run.ttqAngleId === 'multi_solution';
}

function collectMultiSolutionGroups(session: TtqSession): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const run of session.pathRuns ?? []) {
    if (run.status !== 'done' || !isMultiSolutionRun(run)) continue;
    const sourceId = String(run.sourceParticleId || '').trim()
      || (run.sourceParticleIds ?? []).map((id) => String(id || '').trim()).find(Boolean)
      || '';
    if (!sourceId) continue;
    const existing = groups.get(sourceId) ?? [];
    for (const derivedId of run.derivedParticleIds ?? []) {
      const id = String(derivedId || '').trim();
      if (id && !existing.includes(id)) existing.push(id);
    }
    groups.set(sourceId, existing);
  }
  return groups;
}

function particleTitle(session: TtqSession, particleId: string): string {
  const index = session.particles?.findIndex((item) => item.particleId === particleId) ?? 0;
  const entry = session.particles?.find((item) => item.particleId === particleId);
  if (!entry) return particleId.slice(0, 8);
  return resolveTtqParticleTitle(entry, Math.max(index, 0));
}

function layerLabelForSource(session: TtqSession, sourceId: string, derivedIds: string[]): string {
  const title = particleTitle(session, sourceId);
  if (derivedIds.length === 0) return title;
  return `${title} · ${derivedIds.length} 衍生`;
}

function buildSourceLayer(
  session: TtqSession,
  sourceId: string,
  derivedIds: string[],
  z: number,
): TtqibLayer {
  return {
    id: `${TTQIB_LAYER_SOURCE_PREFIX}${sourceId}`,
    label: layerLabelForSource(session, sourceId, derivedIds),
    sourceParticleIds: [sourceId, ...derivedIds],
    z,
  };
}

function buildDerivedLayer(
  session: TtqSession,
  pathRunId: string,
  particleIds: string[],
  z: number,
): TtqibLayer {
  const label = particleIds.length > 1
    ? `跨层品题 · ${particleIds.length} 颗粒`
    : particleTitle(session, particleIds[0] ?? pathRunId);
  return {
    id: derivedLayerId(pathRunId),
    label,
    sourceParticleIds: particleIds,
    z,
  };
}

export function buildDefaultLayers(session: TtqSession): TtqibLayer[] {
  // 一对多主画布不展示多对一收敛代表题（cttq.*）；它们只出现在鸟瞰
  const convergeIds = collectCttqConvergeParticleIds(session);
  const allIds = (session.particles ?? [])
    .map((entry) => entry.particleId)
    .filter((id) => !convergeIds.has(id));
  return [{
    id: 'layer:main',
    label: '主画布',
    sourceParticleIds: allIds,
    z: 0,
  }];
}

function readLegacyNodePosition(
  nodes: TtqibNodeLayout[] | undefined,
  particleId: string,
): { x: number; y: number } | null {
  const node = nodes?.find((item) => item.refId === particleId || item.nodeId === `particle:${particleId}`);
  return node?.position ?? null;
}

import {
  estimateWholeHeight,
  reflowAllObjects,
  detectParticleOverlap,
  TTQIB_CARD_WIDTH,
  TTQIB_COLUMN_GAP,
  TTQIB_LAYER_TOP,
} from './ttqibObjectLayout';
import { isViewportUsable, normalizeViewport } from './ttqibViewport';
import { collectCttqConvergeParticleIds, normalizeParticleNumberBook } from './ttqibParticleNumbers';

export function buildDefaultObjectsForLayer(
  session: TtqSession,
  layer: TtqibLayer,
  legacyNodes?: TtqibNodeLayout[],
  savedObjects?: TtqibCanvasObject[],
): TtqibCanvasObject[] {
  const savedById = new Map((savedObjects ?? []).map((item) => [item.id, item]));
  const objects: TtqibCanvasObject[] = [];

  layer.sourceParticleIds.forEach((particleId, columnIndex) => {
    const entry = session.particles?.find((item) => item.particleId === particleId);
    if (!entry) return;

    const legacyPos = readLegacyNodePosition(legacyNodes, particleId);
    const baseX = legacyPos?.x ?? 80 + columnIndex * TTQIB_COLUMN_GAP;
    const baseY = legacyPos?.y ?? TTQIB_LAYER_TOP;

    const wholeId = wholeObjectId(particleId);
    const savedWhole = savedById.get(wholeId);
    objects.push({
      id: wholeId,
      type: 'particle-whole',
      layerId: layer.id,
      particleId,
      x: savedWhole?.x ?? baseX,
      y: savedWhole?.y ?? baseY,
      width: savedWhole?.width ?? TTQIB_CARD_WIDTH,
      height: savedWhole?.height ?? estimateWholeHeight(entry),
    });

    // 爆炸文本段身份；坐标跟随整卡
    const display = resolveKnowledgeParticleQaDisplay({
      questionSource: entry.particleQuestions,
      answerSource: null,
    }).questionDisplay;
    const fullText = [display.main, ...display.items].filter(Boolean).join('\n').trim()
      || String(entry.particleTitle || '').trim();
    // 延迟引入避免循环依赖：用简易切分兜底，完整爆炸在卡片侧做
    const roughChunks = fullText
      ? fullText.split(/(?<=[，,。；;？?！!])\s*/).map((s) => s.trim()).filter(Boolean)
      : [];
    const chunks = roughChunks.length > 1 ? roughChunks : (fullText ? [fullText] : []);
    chunks.forEach((_text, index) => {
      const segId = segmentObjectId(particleId, index);
      const savedSeg = savedById.get(segId);
      objects.push({
        id: segId,
        type: 'text-segment',
        layerId: layer.id,
        particleId,
        segmentIndex: index,
        segmentId: savedSeg?.segmentId,
        x: savedWhole?.x ?? baseX,
        y: savedWhole?.y ?? baseY,
        width: savedWhole?.width ?? TTQIB_CARD_WIDTH,
        hidden: savedSeg?.hidden,
      });
    });

    const imgId = imageObjectId(particleId);
    const savedImg = savedById.get(imgId);
    objects.push({
      id: imgId,
      type: 'embedded-image',
      layerId: layer.id,
      particleId,
      x: savedWhole?.x ?? baseX,
      y: savedWhole?.y ?? baseY,
      width: savedWhole?.width ?? TTQIB_CARD_WIDTH,
      hidden: savedImg?.hidden,
    });
  });

  return objects;
}

function isModernState(raw: LegacyRawState): boolean {
  return Array.isArray(raw.layers) && raw.layers.length > 0 && Array.isArray(raw.objects);
}

export function normalizeTtqibState(
  session: TtqSession,
  rawInput?: LegacyRawState | null,
): TtqibState {
  const raw = { ...(rawInput ?? {}) };
  delete raw.excalidrawScene;

  if (isModernState(raw)) {
    const needsRebuild = (raw.layoutRevision ?? 0) < SCHEMA_VERSION;
    const layers = needsRebuild ? buildDefaultLayers(session) : (raw.layers ?? []);
    let objects = raw.objects ?? [];
    if (needsRebuild) {
      // 按新图层规则重建整卡 + 段身份，保留整卡坐标
      const rebuilt = layers.flatMap((layer) => buildDefaultObjectsForLayer(
        session,
        layer,
        undefined,
        objects,
      ));
      objects = reflowAllObjects(session, layers, rebuilt);
    } else if (detectParticleOverlap(objects)) {
      objects = reflowAllObjects(session, layers, objects);
    }
    const activeLayerId = raw.activeLayerId && layers.some((layer) => layer.id === raw.activeLayerId)
      ? raw.activeLayerId
      : layers[0]?.id;
    const normalizedViewport = normalizeViewport(raw.viewport);
    // 不因 schema 重建清空视口：保留用户上次缩放/平移
    const viewportInitialized = Boolean(
      raw.viewportInitialized
      && typeof raw.viewport?.zoom === 'number'
      && isViewportUsable(normalizedViewport),
    );
    return {
      viewport: normalizedViewport,
      viewportInitialized,
      activeLayerId,
      layers,
      objects,
      edges: raw.edges ?? [],
      associations: Array.isArray(raw.associations) ? raw.associations : [],
      particleNumbers: normalizeParticleNumberBook(raw.particleNumbers),
      birdsEyePinnedFloats: Array.isArray(raw.birdsEyePinnedFloats)
        ? raw.birdsEyePinnedFloats
          .map((item) => ({
            particleId: String(item?.particleId || '').trim(),
            left: Number(item?.left),
            top: Number(item?.top),
          }))
          .filter((item) => item.particleId && Number.isFinite(item.left) && Number.isFinite(item.top))
        : [],
      layoutRevision: SCHEMA_VERSION,
      updatedAt: raw.updatedAt,
    };
  }

  const legacyNodes = raw.nodes ?? [];
  const layers = buildDefaultLayers(session);
  let layerObjects = layers.flatMap((layer) => buildDefaultObjectsForLayer(
    session,
    layer,
    legacyNodes,
    raw.objects,
  ));
  layerObjects = reflowAllObjects(session, layers, layerObjects);

  return {
    viewport: normalizeViewport(raw.viewport),
    viewportInitialized: Boolean(
      raw.viewportInitialized
      && typeof raw.viewport?.zoom === 'number'
      && isViewportUsable(raw.viewport),
    ),
    activeLayerId: layers[0]?.id,
    layers,
    objects: layerObjects,
    edges: [],
    associations: Array.isArray(raw.associations) ? raw.associations : [],
    particleNumbers: normalizeParticleNumberBook(raw.particleNumbers),
    birdsEyePinnedFloats: Array.isArray(raw.birdsEyePinnedFloats)
      ? raw.birdsEyePinnedFloats
        .map((item) => ({
          particleId: String(item?.particleId || '').trim(),
          left: Number(item?.left),
          top: Number(item?.top),
        }))
        .filter((item) => item.particleId && Number.isFinite(item.left) && Number.isFinite(item.top))
      : [],
    layoutRevision: SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
  };
}

export { buildDerivedLayer, collectMultiSolutionGroups };
